import { Box, Collapse, Divider, RotatingArrowButton, Stack, Typography } from '@packages/shared';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { InstallmentValueTilesContainer } from '../../../atoms/InstallmentValueTilesContainer';
import { PaymentPause } from '../../../atoms/PaymentPause';
import { LegalHints } from '../../../atoms/LegalHints/LegalHints.client';
import { InstallmentPriceBox } from '../../../atoms/InstallmentPriceBox';
import { PaymentPlan } from '../../../atoms/PaymentPlan';
import { CallapsibleMessages } from './messages';
import type { InstallmentCalulatorLayoutComponentsDefaultProps } from '../types';
import { Slider } from '../../../atoms/Slider';
import { InfoCallToAction } from '../../../atoms/InfoCallToAction';
import { LegalHintsImage } from '../../../atoms/LegalHintsImage';

export type CollapsibleProps = InstallmentCalulatorLayoutComponentsDefaultProps & {
  /**
   * should the payment plan section be collapsed/closed
   */
  paymentPlanCollapsed?: boolean;
};

/**
 * Calculator component
 *
 * component to display the fixed installment calculator
 *
 * */
export const Collapsible = ({
  calculationResult,
  calculationValuta,
  isLoading,
  paymentPlanCollapsed = true,
  hasEditablePrice,
  onChangeInstallmentValue,
  onChangePaymentPauseValue,
  onChangeBasePriceValue,
}: CollapsibleProps) => {
  const {
    currency,
    singleInstallment,
    viableInstallments,
    numberOfInstallments,
    paymentPause,
    valuta,
    supportsPaymentPause,
    type,
    minValue,
    maxValue,
  } = calculationResult;
  const {
    installmentCalculator: { hasLegalHints, legalHintsImage, hasInfoCallToAction },
  } = useConfig();
  const { formatMessage } = useIntl();

  const [isCollapsed, setIsCollapsed] = useState(paymentPlanCollapsed);

  useEffect(() => {
    setIsCollapsed(paymentPlanCollapsed);
  }, [paymentPlanCollapsed]);

  function handleCollapsed() {
    setIsCollapsed((prev) => !prev);
  }

  return (
    <Stack direction="column" flexWrap="wrap" gap={1.5} mt={0} mb={4} p={2}>
      <InstallmentPriceBox
        currency={currency}
        calculationValuta={calculationValuta}
        singleInstallment={singleInstallment}
        isLoading={isLoading}
        hasEditablePrice={hasEditablePrice}
        onChangeBasePriceValue={onChangeBasePriceValue}
        minValue={minValue}
        maxValue={maxValue}
      />
      {hasInfoCallToAction && <InfoCallToAction />}
      {type === 'FIX' && viableInstallments ? (
        <InstallmentValueTilesContainer
          installmentsList={viableInstallments}
          activeInstallment={numberOfInstallments}
          installmentSelectedHandler={onChangeInstallmentValue}
        />
      ) : (
        <Slider
          viableInstallments={viableInstallments}
          numberOfInstallments={numberOfInstallments}
          installmentSelectedHandler={onChangeInstallmentValue}
        />
      )}
      <Collapse in={!isCollapsed}>
        {supportsPaymentPause && (
          <>
            <Divider sx={{ mt: 3, mb: 1.5 }} />
            <Box mb={1}>
              <PaymentPause checked={paymentPause} checkHandler={onChangePaymentPauseValue} />
            </Box>
          </>
        )}
        <PaymentPlan asBox={false} calculationResult={calculationResult} />
        {hasLegalHints && <LegalHints paymentPauseValuta={valuta} style={{ marginTop: '24px' }} />}
      </Collapse>
      <Box>
        <Divider flexItem orientation="horizontal" sx={{ mx: -2 }} />
        <Stack
          direction="row"
          flexWrap="nowrap"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleCollapsed()}
          py={2}
        >
          <Typography variant="body2">
            {formatMessage(
              isCollapsed ? CallapsibleMessages.Maximize : CallapsibleMessages.Minimize,
            )}
          </Typography>
          <RotatingArrowButton
            rotated={!isCollapsed}
            sx={{
              ml: 1.125,
            }}
          />
        </Stack>
      </Box>
      {legalHintsImage !== null && <LegalHintsImage />}
    </Stack>
  );
};
